import { billingProviderSchema } from "../billing/contracts";
import { z } from "zod";
export const saasConfigurationDefaults = {
    registration: {
        workflowId: "e00ffe4a-e3cf-4803-b2cc-1f3ba6fa69f7",
        entityDefinitionId: "8be90815-14ff-4831-849f-eafcc03f25a1",
        verificationEntityDefinitionId: "841d8757-72e3-49a3-9552-41df8f810b2f",
        sendEmailVerificationFunction: "sendEmailVerificationCode",
        verifyEmailFunction: "saasVerifyEmail",
        approvePaymentFunction: "approvePayment",
        // Empty disables the workspace-code step; the registration form hides it.
        workspaceCodeAvailabilityFunction: "",
        salesPack: "free",
        // Empty accepts any URL-selected offer; otherwise unlisted offers use salesPack.
        salesPacks: [],
        salesPacksWithoutWorkspace: [],
        includeGeneratedTenant: true,
        consentText: "I agree to the Terms of Service and Privacy Policy and consent to the processing of my account data.",
    },
    auth: {
        googleClientId: "",
        facebookAppId: "",
        oauthRedirectUri: "",
    },
    functions: {
        accountOperations: "accountOps",
        pushSubscriptionOperations: "pushSubscriptionOperations",
        subscriptionOperations: "subscriptionOps",
    },
    payments: {
        defaultProvider: "paddle",
        paddle: {
            environment: "sandbox",
            clientToken: "",
            operationFunction: "paddleCheckout",
            priceResolution: "payment-option",
        },
    },
    routes: {
        home: "/",
        login: "/auth/login",
        register: "/auth/register",
        resetPassword: "/auth/reset-password",
        registrationComplete: "/app/onboarding",
        account: "/app/account",
        pricing: "/pricing",
    },
    i18n: {
        defaultLanguage: "en-US",
        supportedLanguages: ["en-US"],
        languagePathPrefix: true,
    },
    branding: {
        productName: "SaaS",
        logoUrl: "",
        legalName: "SaaS",
        supportEmail: "",
    },
    storage: {
        authTokenKey: "saas_auth_token",
        authUserKey: "saas_auth_user",
        registrationStateKey: "saas_registration_state",
        referralParametersKey: "referral_params",
        registrationExpiryHours: 24,
    },
    notifications: {
        firebase: {
            apiKey: "",
            appId: "",
            authDomain: "",
            messagingSenderId: "",
            projectId: "",
            storageBucket: "",
        },
        vapidPublicKey: "",
        capacitorBuild: false,
        operationName: "pushSubscriptionOperations",
    },
    analytics: {
        enabled: false,
        measurementId: "",
    },
    pwa: {
        serviceWorkerUrl: "/sw.js",
        updateIntervalMs: 60 * 60 * 1_000,
    },
    features: [],
    plans: {},
    build: {
        development: false,
        version: "",
        buildDate: "",
    },
};
const saasConfigurationValuesSchema = z.object({
    core: z.object({
        apiUrl: z.string().url(),
        tenantCode: z.string().trim().min(1).optional(),
    }),
    registration: z.object({
        workflowId: z.string().uuid(),
        entityDefinitionId: z.string().uuid(),
        verificationEntityDefinitionId: z.string().uuid(),
        sendEmailVerificationFunction: z.string().trim().min(1),
        verifyEmailFunction: z.string().trim().min(1),
        approvePaymentFunction: z.string().trim().min(1),
        workspaceCodeAvailabilityFunction: z.string().trim(),
        salesPack: z.string().trim().min(1),
        salesPacks: z.array(z.string().trim().min(1)),
        salesPacksWithoutWorkspace: z.array(z.string().trim().min(1)),
        includeGeneratedTenant: z.boolean(),
        consentText: z.string().trim().min(1),
    }),
    auth: z.object({
        googleClientId: z.string(),
        facebookAppId: z.string(),
        oauthRedirectUri: z.union([z.literal(""), z.string().url()]),
    }),
    functions: z.object({
        accountOperations: z.string().trim().min(1),
        pushSubscriptionOperations: z.string().trim().min(1),
        subscriptionOperations: z.string().trim().min(1),
    }),
    payments: z.object({
        defaultProvider: billingProviderSchema,
        paddle: z.object({
            environment: z.enum(["sandbox", "production"]),
            clientToken: z.string(),
            operationFunction: z.string().trim().min(1),
            priceResolution: z.enum(["payment-option", "payment-option-or-items"]),
        }),
    }),
    routes: z.object({
        home: z.string().startsWith("/"),
        login: z.string().startsWith("/"),
        register: z.string().startsWith("/"),
        resetPassword: z.string().startsWith("/"),
        registrationComplete: z.string().startsWith("/"),
        account: z.string().startsWith("/"),
        pricing: z.string().startsWith("/"),
    }),
    i18n: z.object({
        defaultLanguage: z.string().trim().min(1),
        supportedLanguages: z.array(z.string().trim().min(1)).min(1),
        languagePathPrefix: z.boolean(),
    }),
    branding: z.object({
        productName: z.string().trim().min(1),
        logoUrl: z.string(),
        legalName: z.string().trim().min(1),
        supportEmail: z.union([z.literal(""), z.string().email()]),
    }),
    storage: z.object({
        authTokenKey: z.string().trim().min(1),
        authUserKey: z.string().trim().min(1),
        registrationStateKey: z.string().trim().min(1),
        referralParametersKey: z.string().trim().min(1),
        registrationExpiryHours: z.number().positive(),
    }),
    notifications: z.object({
        firebase: z.object({
            apiKey: z.string(),
            appId: z.string(),
            authDomain: z.string(),
            messagingSenderId: z.string(),
            projectId: z.string(),
            storageBucket: z.string(),
        }),
        vapidPublicKey: z.string(),
        capacitorBuild: z.boolean(),
        operationName: z.string().trim().min(1),
    }),
    analytics: z.object({
        enabled: z.boolean(),
        measurementId: z.string(),
    }),
    pwa: z.object({
        serviceWorkerUrl: z.string().startsWith("/"),
        updateIntervalMs: z.number().int().positive(),
    }),
    features: z.array(z.object({
        id: z.string().trim().min(1),
        name: z.string().trim().min(1),
        description: z.string(),
        availableInTiers: z.array(z.string().trim().min(1)),
        editRoles: z.array(z.string().trim().min(1)),
    })),
    plans: z.record(z.string(), z.object({
        name: z.string().trim().min(1),
        description: z.string(),
        features: z.array(z.string()),
    })),
    build: z.object({
        development: z.boolean(),
        version: z.string(),
        buildDate: z.string(),
    }),
});
function mergeValues(input) {
    return {
        ...saasConfigurationDefaults,
        ...input,
        core: { ...input.core },
        registration: {
            ...saasConfigurationDefaults.registration,
            ...input.registration,
        },
        auth: { ...saasConfigurationDefaults.auth, ...input.auth },
        functions: { ...saasConfigurationDefaults.functions, ...input.functions },
        payments: {
            ...saasConfigurationDefaults.payments,
            ...input.payments,
            paddle: {
                ...saasConfigurationDefaults.payments.paddle,
                ...input.payments?.paddle,
            },
        },
        routes: { ...saasConfigurationDefaults.routes, ...input.routes },
        i18n: { ...saasConfigurationDefaults.i18n, ...input.i18n },
        branding: { ...saasConfigurationDefaults.branding, ...input.branding },
        storage: {
            ...saasConfigurationDefaults.storage,
            ...input.storage,
        },
        notifications: {
            ...saasConfigurationDefaults.notifications,
            ...input.notifications,
            firebase: {
                ...saasConfigurationDefaults.notifications.firebase,
                ...input.notifications?.firebase,
            },
        },
        analytics: { ...saasConfigurationDefaults.analytics, ...input.analytics },
        pwa: { ...saasConfigurationDefaults.pwa, ...input.pwa },
        features: input.features ?? saasConfigurationDefaults.features,
        plans: { ...saasConfigurationDefaults.plans, ...input.plans },
        build: { ...saasConfigurationDefaults.build, ...input.build },
    };
}
function deepFreeze(value) {
    if (typeof value !== "object" || value === null || Object.isFrozen(value)) {
        return value;
    }
    for (const child of Object.values(value)) {
        deepFreeze(child);
    }
    return Object.freeze(value);
}
export function createSaasConfiguration(input) {
    const values = saasConfigurationValuesSchema.parse(mergeValues(input));
    return deepFreeze({ ...values, adapters: { ...input.adapters } });
}
//# sourceMappingURL=saasConfiguration.js.map